<template>
    <div class="w-100">
        <!-- Page title -->
        <div class="row align-items-center mt-1 mb-3">
            <div class="col-9">
                <h2 class="page-title">
                    {{ pageTitle }}
                </h2>
            </div>
            <!-- Progress bar -->
            <div v-if="progressBar == true" class="col-3 ms-auto d-print-none d-flex align-items-center" id="progressBar">
                <span class="small me-3 w-50 text-end">{{ percentageComplete }}% Complete</span>
                <b-progress class="w-50" :value="currentStep" :max="maxStep" animated></b-progress>
            </div>
        </div>

        <!-- Alert box -->
        <div v-if="this.v$.step[this.currentStep - 1].$error" class="alert alert-danger alert-dismissible" role="alert">
            <div class="d-flex">
                <div>
                    <!-- Download SVG icon from http://tabler-icons.io/i/alert-circle -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon alert-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                        <path d="M12 8v4"></path>
                        <path d="M12 16h.01"></path>
                    </svg>
                </div>
                <div>
                    <span v-if="v$.$errors.length > 1"> Sorry, there are ({{ this.v$.step[this.currentStep - 1].$errors.length }}) errors found please correct the errors to proceed.</span>
                    <span v-if="v$.$errors.length <= 1"> Sorry, there's ({{ this.v$.step[this.currentStep - 1].$errors.length }}) error found please correct the error to proceed.</span>
                </div>
            </div>
            <a class="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
        </div>

        <form id="create-accident-incident-report" action="accident-incident-reports/store" class="add-accident-incident-report add-record">
            <div class="card" v-if="currentStep == 1" title="Accident Incident Details">
                <div class="card-header">
                    <h3 class="card-title">Accident / Incident Details</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div v-if="!createdFromResidentPage">
                                <div class="mb-3">
                                    <div class="form-label">Who's Involved</div>
                                    <label v-for="option in whosInvolvedOptions" class="form-check form-check-inline">
                                        <input class="form-check-input" type="radio" name="whosInvolved" :value="option" v-model="step[0].whosInvolved" @change="clearNames" />
                                        <span class="form-check-label">{{ option }}</span>
                                    </label>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].whosInvolved.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="mb-3" v-if="step[0].whosInvolved == 'Resident'">
                                    <div class="form-label">Resident</div>
                                    <select
                                        class="form-select text-capitalize"
                                        name="residentID"
                                        v-model="step[0].residentID"
                                        :class="{
                                            'is-invalid': v$.step[0].residentID.$invalid,
                                        }"
                                    >
                                        <option value="" disabled>Choose one</option>
                                        <option v-for="option in residentOptions" :value="option.id">
                                            {{ option.first_name + " " + option.last_name }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].residentID.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="mb-3" v-if="step[0].whosInvolved != 'Resident'">
                                    <div class="form-label">Name</div>
                                    <input type="text" class="form-control w-75" name="name" placeholder="Name" v-model="step[0].name" :class="{'is-invalid': v$.step[0].name.$invalid}" />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].name.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Nature of Problem</div>

                                <multiselect
                                    name="natureOfProblem"
                                    v-model="step[0].natureOfProblem"
                                    :class="{'is-invalid': v$.step[0].natureOfProblem.$invalid}"
                                    :options="this.natureOfProblemOptions"
                                    :searchable="true"
                                    :close-on-select="true"
                                    :show-labels="true"
                                    :taggable="true"
                                    :multiple="true"
                                    title=""
                                    deselect-label="Deselect"
                                    select-label="Select"
                                    tag-placeholder="Add this as new tag"
                                    placeholder="Select or add a new activity"
                                    @tag="addTagNatureOfProblem"
                                ></multiselect>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].natureOfProblem.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Date of Incident</div>
                                    <input
                                        type="date"
                                        class="form-control w-75"
                                        name="dateOfIncident"
                                        placeholder=""
                                        v-model="step[0].dateOfIncident"
                                        :class="{'is-invalid': v$.step[0].dateOfIncident.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].dateOfIncident.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>

                                <div class="col-6">
                                    <div class="form-label">Time of Incident</div>
                                    <input
                                        type="time"
                                        class="form-control w-75"
                                        name="timeOfIncident"
                                        placeholder="16:00"
                                        v-model="step[0].timeOfIncident"
                                        :class="{'is-invalid': v$.step[0].timeOfIncident.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].timeOfIncident.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Place of incident</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="placeOfIncident"
                                    placeholder="Place of incident"
                                    v-model="step[0].placeOfIncident"
                                    :class="{'is-invalid': v$.step[0].placeOfIncident.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].placeOfIncident.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Witnessed, found or reported by</div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="witnessedBy"
                                        placeholder="Name"
                                        v-model="step[0].witnessedBy"
                                        :class="{'is-invalid': v$.step[0].witnessedBy.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].witnessedBy.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="form-label">Designation</div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="witnessedByDesignation"
                                        placeholder="Designation"
                                        v-model="step[0].witnessedByDesignation"
                                        :class="{'is-invalid': v$.step[0].witnessedByDesignation.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].witnessedByDesignation.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Describe what happened</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="Describe what happened"
                                    value=""
                                    spellcheck="false"
                                    name="describeWhatHappened"
                                    v-model="step[0].describeWhatHappened"
                                    :class="{
                                        'is-invalid': v$.step[0].describeWhatHappened.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].describeWhatHappened.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Contributing Factors (possible cause of accident):</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="Possible cause of accident"
                                    value=""
                                    spellcheck="false"
                                    name="contributingFactors"
                                    v-model="step[0].contributingFactors"
                                    :class="{
                                        'is-invalid': v$.step[0].contributingFactors.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].contributingFactors.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn invisible">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>
            <div class="card" v-if="currentStep == 2" title="Injury Details">
                <div class="card-header">
                    <h3 class="card-title">Injury Details</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Injury</div>
                                <input type="text" class="form-control w-75" name="injury" placeholder="Injury" v-model="step[1].injury" :class="{'is-invalid': v$.step[1].injury.$invalid}" />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[1].injury.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Injury site</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="injurySite"
                                    placeholder="Injury site"
                                    v-model="step[1].injurySite"
                                    :class="{'is-invalid': v$.step[1].injurySite.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[1].injurySite.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">First action taken</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="First action taken"
                                    value=""
                                    spellcheck="false"
                                    name="firstActionTaken"
                                    v-model="step[1].firstActionTaken"
                                    :class="{
                                        'is-invalid': v$.step[1].firstActionTaken.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].firstActionTaken.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Assessment done</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="Assessment done"
                                    value=""
                                    spellcheck="false"
                                    name="assessmentDone"
                                    v-model="step[1].assessmentDone"
                                    :class="{
                                        'is-invalid': v$.step[1].assessmentDone.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].assessmentDone.$invalid}">Invalid feedback</div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Manager notified</div>
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="managerNotified"
                                        placeholder="Name of manager"
                                        v-model="step[1].managerNotified"
                                        :class="{'is-invalid': v$.step[1].managerNotified.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].managerNotified.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="form-label invisible">Notified in person</div>
                                    <div class="form-check form-switch mt-3">
                                        <input class="form-check-input" type="checkbox" name="managerNotifiedInPerson" v-model="step[1].managerNotifiedInPerson" />
                                        <span class="form-check-label">Notified in person</span>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[1].managerNotifiedInPerson.$invalid}">Invalid feedback</div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Registered nurse notified</div>
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="registeredNurseNotified"
                                        placeholder="Name of registered nurse"
                                        v-model="step[1].registeredNurseNotified"
                                        :class="{'is-invalid': v$.step[1].registeredNurseNotified.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].registeredNurseNotified.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="form-label invisible">Notified in person</div>
                                    <div class="form-check form-switch mt-3">
                                        <input class="form-check-input" type="checkbox" name="registeredNurseNotifiedInPerson" v-model="step[1].registeredNurseNotifiedInPerson" />
                                        <span class="form-check-label">Notified in person</span>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[1].registeredNurseNotifiedInPerson.$invalid}">Invalid feedback</div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Name of doctor (if called)</div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="doctorCalled"
                                        placeholder="Name of doctor"
                                        v-model="step[1].doctorCalled"
                                        :class="{'is-invalid': v$.step[1].doctorCalled.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].doctorCalled.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="row">
                                        <div class="col-6">
                                            <div class="form-label">Date</div>
                                            <input
                                                type="date"
                                                class="form-control"
                                                name="doctorCalledDate"
                                                placeholder=""
                                                v-model="step[1].doctorCalledDate"
                                                :class="{'is-invalid': v$.step[1].doctorCalledDate.$invalid}"
                                            />
                                            <div
                                                class="invalid-feedback"
                                                :class="{
                                                    'd-block': v$.step[1].doctorCalledDate.$invalid,
                                                }"
                                            >
                                                Invalid feedback
                                            </div>
                                        </div>

                                        <div class="col-6">
                                            <div class="form-label">Time</div>
                                            <input
                                                type="time"
                                                class="form-control"
                                                name="doctorCalledTime"
                                                placeholder="16:00"
                                                v-model="step[1].doctorCalledTime"
                                                :class="{'is-invalid': v$.step[1].doctorCalledTime.$invalid}"
                                            />
                                            <div
                                                class="invalid-feedback"
                                                :class="{
                                                    'd-block': v$.step[1].doctorCalledTime.$invalid,
                                                }"
                                            >
                                                Invalid feedback
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Staff Signature</div>
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="staffSignature"
                                        placeholder="Type full name to sign"
                                        v-model="step[1].staffSignature"
                                        :class="{'is-invalid': v$.step[1].staffSignature.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].staffSignature.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="form-label">Staff Designation</div>
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="staffDesignation"
                                        placeholder="Staff Designation"
                                        v-model="step[1].staffDesignation"
                                        :class="{'is-invalid': v$.step[1].staffDesignation.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].staffDesignation.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn invisible">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack()" href="#" class="p-2 mx-3 d-block" id="last-back-btn">Back</a>
                        <a @click="onClickNext()" class="ml-5 btn btn-primary" id="submit-btn">Finish</a>
                    </p>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {alpha, alphaNum, email, integer, required, requiredIf, minLength, maxLength, minValue, maxValue, helpers} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import Multiselect from "vue-multiselect";

export default {
    name: "accidentIncidentReportCreateComponent",
    props: ["progressBar", "pageTitle", "residentId", "residentName", "userId", "accidentIncidentReport", "operation", "residents"], // vue can read kebab props as camel cased vars
    components: {Multiselect},
    setup() {
        return {v$: useVuelidate()};
    },
    data() {
        var residentIDs = [];

        for (const entry of JSON.parse(this.residents)) {
            residentIDs.push(entry.id);
        }

        // If copying, assign accidentIncidentReport object to a variable
        if (this.operation == "store") {
            var accidentIncidentReportObject = {};
            var endpointToUse = location.protocol + "//" + window.location.host + "/api/accident-incident-report/" + process.env.MIX_API_KEY;
        } else if (this.operation == "copy") {
            var accidentIncidentReportObject = JSON.parse(this.accidentIncidentReport);
            accidentIncidentReportObject["activity_level"] = JSON.parse(accidentIncidentReportObject["activity_level"]);
            accidentIncidentReportObject["sleep_patterns"] = JSON.parse(accidentIncidentReportObject["sleep_patterns"]);
            accidentIncidentReportObject["mobility"] = JSON.parse(accidentIncidentReportObject["mobility"]);
            accidentIncidentReportObject["registered_nurse_signature"] = JSON.parse(accidentIncidentReportObject["registered_nurse_signature"]);
        }

        // Return empty string when object doesn't exist
        // options - array | string
        function returnValueFromObject(fieldName, parent, defaultValue = "", options) {
            // Editing
            if (!_.isEmpty(accidentIncidentReportObject)) {
                return accidentIncidentReportObject[parent][fieldName];
            } else {
                return defaultValue;
            }
        }

        return {
            currentStep: 1,
            maxStep: 2,
            percentageComplete: 0,
            errors: 0,
            residentOptions: JSON.parse(this.residents),
            residentIDOptions: residentIDs,
            yesNoOptions: ["Yes", "No"],
            trueFalseOptions: [true, false],
            natureOfProblemOptions: [
                "Witnessed Fall",
                "Un-witnessed Incident",
                "Un-witnessed Fall",
                "Skin Tear",
                "Bruising",
                "Possible Fracture",
                "Head Injury",
                "Other Injury",
                "Assault",
                "Altercation/Agitation",
                "Missing Resident",
                "Pressure Injury",
                "Drug Error",
                "Injury to Staff",
                "Injury to Visitor",
                "Damage to Property",
                "Other",
            ],
            whosInvolvedOptions: ["Resident", "Staff", "Visitor"],
            host: document.location.origin,
            endpoint: endpointToUse,
            step: [
                // Step 1 - Accident / Incident Details
                {
                    natureOfProblem: returnValueFromObject("natureOfProblem", "accident_incident_details"),
                    whosInvolved: returnValueFromObject("whosInvolved", "accident_incident_details", this.residentId ? "Resident" : null),
                    name: returnValueFromObject("name", "accident_incident_details"),
                    residentID: this.residentId ? parseInt(this.residentId) : null,
                    dateOfIncident: returnValueFromObject("dateOfIncident", "accident_incident_details"),
                    timeOfIncident: returnValueFromObject("timeOfIncident", "accident_incident_details"),
                    placeOfIncident: returnValueFromObject("placeOfIncident", "accident_incident_details"),
                    witnessedBy: returnValueFromObject("witnessedBy", "accident_incident_details"),
                    witnessedByDesignation: returnValueFromObject("witnessedByDesignation", "accident_incident_details"),
                    describeWhatHappened: returnValueFromObject("describeWhatHappened", "accident_incident_details"),
                    contributingFactors: returnValueFromObject("contributingFactors", "accident_incident_details"),
                },
                // Step 2 - Injury Details
                {
                    injury: returnValueFromObject("injury", "injury_details"),
                    injurySite: returnValueFromObject("injurySite", "injury_details"),
                    firstActionTaken: returnValueFromObject("firstActionTaken", "injury_details"),
                    assessmentDone: returnValueFromObject("assessmentDone", "injury_details"),
                    managerNotified: returnValueFromObject("managerNotified", "injury_details"),
                    managerNotifiedInPerson: returnValueFromObject("managerNotifiedInPerson", "injury_details"),
                    registeredNurseNotified: returnValueFromObject("registeredNurseNotified", "injury_details"),
                    registeredNurseNotifiedInPerson: returnValueFromObject("registeredNurseNotifiedInPerson", "injury_details"),
                    doctorCalled: returnValueFromObject("doctorCalled", "injury_details"),
                    doctorCalledDate: returnValueFromObject("doctorCalledDate", "injury_details"),
                    doctorCalledTime: returnValueFromObject("doctorCalledTime", "injury_details"),
                    staffSignature: returnValueFromObject("staffSignature", "injury_details"),
                    staffDesignation: returnValueFromObject("staffDesignation", "injury_details"),
                },
            ],
            createdFromResidentPage: this.residentId ? true : false,
        };
    },
    validations() {
        // Custom Validators
        const inArray = (array) => (value) => array.includes(value) || !helpers.req(value);
        const isDateFormat = (value) => value.match("^$|((?:[0-9]{2})?[0-9]{2})-(1[0-2]|0?[1-9])-(3[01]|[12][0-9]|0?[1-9])") || !helpers.req(value);
        const isTimeFormat = (value) => value.match("^$|^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$") || !helpers.req(value);
        const isTextWithSpecialCharacters = (value) => value.match("^[a-zA-Z0-9_ !@#$&%^*()`.+,/\\-\r\n\s'\"]*$") || !helpers.req(value);
        const isTextWithSpace = (value) => value.match("^[a-zA-Z0-9_ \s'\"]*$") || !helpers.req(value);

        const dateType = {
            required,
            isDateFormat,
            $lazy: true,
            $autoDirty: true,
        };

        const timeType = {
            required,
            isTimeFormat,
            $lazy: true,
            $autoDirty: true,
        };

        // TEXT TYPES: default: 191, mid:500, long:1000
        function text(minimum = 3, maximum = 191, isRequired = false) {
            var rule = {
                required: requiredIf(isRequired),
                isTextWithSpecialCharacters,
                minLength: minLength(minimum),
                maxLength: maxLength(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            return rule;
        }

        // INT TYPES:
        function intType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                integer,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // DECIMAL TYPES:
        function decimalType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                decimal,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // ARRAY TYPES
        function inOptions(options, isRequired = false) {
            var rule = {
                required,
                inOptions: inArray(options),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // Use for types with no 'required' parameter
        function notRequired(type) {
            var validations = Object.assign({}, type);
            delete validations["required"];

            return validations;
        }

        return {
            step: [
                // Step 1 - Accident / Incident Description
                {
                    natureOfProblem: {
                        // $each: helpers.forEach({ //TODO: figure out why we can't validate each entry
                        //     minLength: minLength(3),
                        //     required
                        // }),
                        $lazy: true,
                        $autoDirty: true,
                        required,
                    },
                    whosInvolved: inOptions(this.whosInvolvedOptions, true),
                    name: text(3, 191, this.step[0].whosInvolved != "Resident" ? true : false),
                    residentID: inOptions(this.residentIDOptions, this.step[0].whosInvolved == "Resident" ? true : false),

                    dateOfIncident: notRequired(dateType),
                    timeOfIncident: notRequired(timeType),
                    placeOfIncident: text(3, 191),
                    witnessedBy: text(3, 191),
                    witnessedByDesignation: text(3, 191),
                    describeWhatHappened: text(3, 500),
                    contributingFactors: text(3, 500),
                },

                // Step 2 - Injury Details
                {
                    injury: text(3, 191),
                    injurySite: text(3, 191),
                    firstActionTaken: text(3, 191),
                    assessmentDone: text(3, 500),
                    managerNotified: text(3, 191),
                    managerNotifiedInPerson: inOptions(this.trueFalseOptions, false),
                    registeredNurseNotified: text(3, 191),
                    registeredNurseNotifiedInPerson: inOptions(this.trueFalseOptions, false),
                    doctorCalled: text(3, 191),
                    doctorCalledDate: notRequired(dateType),
                    doctorCalledTime: notRequired(timeType),
                    staffSignature: text(3, 191),
                    staffDesignation: text(3, 191),
                },
            ],
        };
    },

    computed: {},

    methods: {
        onClickNext: function () {
            // Validate fields on current step
            this.v$.step[this.currentStep - 1].$touch();

            // if there are no errors in the current step
            if (!this.v$.step[this.currentStep - 1].$error) {
                // All steps
                if (this.currentStep < this.maxStep) {
                    // Proceed to next step
                    this.currentStep++;

                    // Final step
                } else if (this.currentStep == this.maxStep) {
                    // Initialize alerts and toast notifications for displaying message
                    const toastError = Swal.mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: true,
                    });
                    const alertSuccess = Swal.mixin({
                        position: "center",
                        showConfirmButton: true,
                        timer: 3000,
                        timerProgressBar: true,
                    });

                    // Show button spinner
                    document.getElementById("submit-btn").innerHTML = '<i class="fa fa-circle-notch fa-spin"></i>';
                    document.getElementById("submit-btn").disabled = true;
                    document.getElementById("last-back-btn").setAttribute("style", "display:none !important");
                    // document.getElementById("last-save-btn").setAttribute("style", "display:none !important");

                    // Loop through all fields
                    const formData = {};

                    for (let i = 0; i < this.maxStep; i++) {
                        for (const [key, value] of Object.entries(this.step[i])) {
                            // formData.append(key, value);
                            formData[key] = value;
                        }
                    }

                    // Add residentID and userID
                    formData["residentID"] = formData["residentID"] ?? parseInt(this.residentId); // if residentId is selected on the form
                    formData["userID"] = this.userId;

                    if (this.operation == "store") {
                        this.postAccidentIncidentReport(formData, toastError, alertSuccess);
                    }
                }
                this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
            }
        },

        postAccidentIncidentReport: function (formData, toastError, alertSuccess) {
            console.log(formData);
            // Call the store endpoint
            axios
                .post(this.endpoint, formData)
                .then((response) => {
                    if (response.data.error == 403) {
                        console.log("Bad Request");
                        console.log(response.data);

                        // Show bad request toast message. This error occurs when the input fields are not in the expected format
                        toastError.fire({
                            type: "warning",
                            title: "The input fields are malformed and cant be processed.",
                        });
                    } else {
                        if (response.data.id == null) {
                            console.log("Validation Error");
                            console.log(response.data);

                            // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the resident object
                            toastError.fire({
                                type: "warning",
                                title: "There are validation errors found on the form.",
                            });
                        } else {
                            console.log("Success");
                            console.log(response.data);

                            // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the resident object was added to the DB. Thi
                            this.formSuccess = true;
                            window.residentId = parseInt(this.residentId);

                            alertSuccess
                                .fire({
                                    title: "Success!",
                                    text: "The accident / incident report has been added.",
                                    icon: "success",
                                })
                                .then(function () {
                                    if (formData["whosInvolved"] == "Resident") {
                                        window.location = "/residents/" + formData["residentID"] + "/accident-incident-reports";
                                    } else {
                                        window.location = "/accident-incident-reports";
                                    }
                                });
                        }
                    }
                })
                .catch((err) => {
                    console.log("Endpoint Error!");
                    console.log(err.response);
                    toastError.fire({type: "warning", title: "Sorry, request failed to submit."}); // this error occurs when endpoint is wrong
                });
        },

        onClickBack: function () {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
            this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
        },
        addTagNatureOfProblem(newTag) {
            this.natureOfProblemOptions.push(newTag);
            this.step[0].natureOfProblem.push(newTag);
        },

        clearNames() {
            if (this.step[0].whosInvolved == "Resident") {
                this.step[0].name = "";
            } else {
                this.step[0].residentID = null;
            }
        },
    },
};
</script>
